import { makeAutoObservable, runInAction } from 'mobx';
import { LegacyUserEntry } from '../types/legacyAdminApi';
import passKeyHelper from '../helpers/passKey';
import api from '../api/api';
import logger from '../helpers/logger';
import { store } from './store';
import { CashBoxTotalsDto } from '../typegen/cash-box-totals-dto';

export default class BPKManagerStore {
	private _isLoading: boolean = false;
	private _authenticatedUser: LegacyUserEntry | null = null;
	private _cashBoxTotals: CashBoxTotalsDto | null = null;

	constructor() {
		makeAutoObservable(this);
	}

	get isLoading() {
		return this._isLoading;
	}

	setIsLoading = (value: boolean) => {
		this._isLoading = value;
	};

	get isAuthenticated() {
		return this._authenticatedUser !== null;
	}

	get authenticatedUser() {
		return this._authenticatedUser;
	}

	get userIsCashCollector() {
		return this._authenticatedUser?.isCashCollector || false;
	}

	get cashBoxTotals() {
		return this._cashBoxTotals;
	}

	logout = () => {
		this._authenticatedUser = null;
	};

	legacyGetUserList = async (): Promise<LegacyUserEntry[]> => {
		const { kioskId, companyId } = store.kioskStore;
		const kioskInfo = {
			kioskID: kioskId,
			customerID: companyId,
			passKey: await passKeyHelper.generatePassKey(),
		};

		let userList: LegacyUserEntry[] = [];
		try {
			userList = await api.legacyBpkAdmin.getUserList(kioskInfo);
			return userList;
		} catch (error) {
			logger.logGeneral.error({}, 'Error getting user list from legacy API:', error);
			return [];
		} finally {
			logger.logGeneral.log({}, 'Retrieved user list from legacy API', userList);
		}
	};

	legacyCheckPin = async (user: LegacyUserEntry, pin: string): Promise<boolean> => {
		const { kioskId, companyId } = store.kioskStore;
		const checkPinRequest = {
			kioskid: kioskId,
			twoFactorIdentification: false,
			photoLink: '',
			customerID: companyId,
			guid: user.userID,
			pin: pin,
			passKey: await passKeyHelper.generatePassKey(),
		};

		let isPinValid = false;
		try {
			isPinValid = await api.legacyBpkAdmin.checkPin(checkPinRequest);
			return isPinValid;
		} catch (error) {
			isPinValid = false;
			logger.logGeneral.error({}, 'Error checking pin from legacy API:', error);
			return isPinValid;
		} finally {
			runInAction(() => {
				if (isPinValid) this._authenticatedUser = user;
				else this._authenticatedUser = null;
				logger.logGeneral.log({}, 'Pin validated:', isPinValid);
			});
		}
	};

	loadCashBoxTotals = async () => {
		let totals: CashBoxTotalsDto | null = null;
		try {
			totals = await api.cashBox.getTotals();
		} catch (error) {
			logger.logGeneral.error({}, 'Error loading cash box totals:', error);
			totals = null;
		} finally {
			runInAction(() => {
				this._cashBoxTotals = totals;
			});
		}
	};

	isCashBoxFull = async (additionalCount: number = 0): Promise<boolean> => {
		try {
			await this.loadCashBoxTotals();
			const billCounts = [
				this._cashBoxTotals?.ones ?? 0,
				this._cashBoxTotals?.twos ?? 0,
				this._cashBoxTotals?.fives ?? 0,
				this._cashBoxTotals?.tens ?? 0,
				this._cashBoxTotals?.twenties ?? 0,
				this._cashBoxTotals?.fifties ?? 0,
				this._cashBoxTotals?.hundreds ?? 0,
			];
			const currentCount = billCounts.reduce((acc, count) => acc + count, 0) + additionalCount;
			logger.logCashAcceptor.log({}, 'Cash Box Totals Loaded', {
				currentCount: currentCount,
				capacity: this._cashBoxTotals?.capacity ?? 0,
				isFull: this._cashBoxTotals ? currentCount >= this._cashBoxTotals.capacity : false,
			});
			return this._cashBoxTotals ? currentCount >= this._cashBoxTotals.capacity : false;
		} catch (error) {
			logger.logGeneral.error({}, 'Error checking if cash box is full:', error);
			return false;
		}
	};
}
